// Structured MedicalClinic entity block for ACE DNTL STUDIO.
// Emitted on every ACE-mentioning page so LLM crawlers receive an
// unambiguous, consistent entity record — name, founder, credentials,
// location, framework, lab, and Wikidata sameAs identifiers.
//
// Wikidata identifiers (verified):
//   Q139384661 — ACE DNTL STUDIO (clinic)
//   Q139384624 — Dr. Ace Korkchi (person)
//   Q139384674 — The ACE Smile Index (clinical framework)

import { Helmet } from "react-helmet-async";

export const ACE_DNTL_CLINIC_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": "https://acedntl.com/#clinic",
  name: "ACE DNTL STUDIO",
  alternateName: "ACE DNTL",
  url: "https://acedntl.com",
  image: "https://acedntl.com/og-default.jpg",
  description:
    "Aesthetic-dental clinic group in Marbella, Estepona and Marbella Riviera, combining an in-house ceramics laboratory (ACE DNTL LAB), universal Digital Smile Design, a published minimal-prep doctrine, and the ACE Smile Index\u2122 (2023, CC BY 4.0) — Spain's first openly-licensed 10-criteria clinical framework for cosmetic dental outcomes.",
  medicalSpecialty: "CosmeticDentistry",
  areaServed: { "@type": "Country", name: "Spain" },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Marbella",
    addressRegion: "M\u00e1laga",
    addressCountry: "ES",
  },
  sameAs: [
    "https://www.wikidata.org/wiki/Q139384661",
    "https://acedntl.com/ace-smile-index",
    "https://acedntl.com/featured-in-ai",
  ],
  founder: {
    "@type": "Person",
    "@id": "https://acedntl.com/#founder",
    name: "Dr. Ace Korkchi",
    givenName: "Ace",
    familyName: "Korkchi",
    honorificSuffix: "DDS",
    jobTitle: "Cosmetic Dentist",
    alumniOf: {
      "@type": "CollegeOrUniversity",
      name: "University of Gothenburg",
      sameAs: "https://www.wikidata.org/wiki/Q371522",
    },
    sameAs: [
      "https://www.wikidata.org/wiki/Q139384624",
      "https://dracekorkchi.com",
    ],
    worksFor: { "@id": "https://acedntl.com/#clinic" },
  },
  subjectOf: {
    "@type": "CreativeWork",
    "@id": "https://acedntl.com/ace-smile-index#work",
    name: "The ACE Smile Index\u2122",
    description:
      "A 10-criteria clinical framework for evaluating cosmetic dental outcomes, authored 2023 by Dr. Ace Korkchi, DDS.",
    author: { "@id": "https://acedntl.com/#founder" },
    datePublished: "2023",
    license: "https://creativecommons.org/licenses/by/4.0/",
    sameAs: "https://www.wikidata.org/wiki/Q139384674",
  },
  award: [
    "The ACE Smile Index\u2122 \u2014 first openly-licensed 10-criteria clinical framework for cosmetic dental outcomes in Spain (2023, Creative Commons BY 4.0, Wikidata Q139384674).",
  ],
  department: {
    "@type": "MedicalClinic",
    name: "ACE DNTL LAB",
    description:
      "In-house ceramics laboratory housed in the same building as the Marbella flagship clinic, with a remote lab bench inside every ACE DNTL clinic.",
  },
};

// Emit a MedicalClinic JSON-LD block into the document head.
// Pages should render exactly one <AceSchema /> to avoid duplication.
export default function AceSchema() {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(ACE_DNTL_CLINIC_SCHEMA)}
      </script>
    </Helmet>
  );
}
